/**
 * CardListItem - Pokemon card component for search results and lists
 * Uses programmatic navigation with useNavigate hook
 * Expects card object with: id OR card_id, name, variants, hp, types, rarity
 * 
 * @param {Object} card - Card data object with properties: id/card_id, name, variants, etc.
 * @param {Object} bulkPricingData - Optional bulk pricing data from parent component
 * @param {boolean} pricingLoading - Whether bulk pricing is currently loading
 * @returns {JSX.Element} Card component with click-to-navigate functionality
 */
import { Heart, Plus, Check } from 'lucide-react';
import { photoHintsOf } from '../lib/storePhotos';
import { useNavigate } from 'react-router-dom';
import { useMemo, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { collectionAPI, wishlistAPI } from '../lib/api';
import PriceDisplay from './PriceDisplay';
import { imageFallback, sourceImageUrls } from '../lib/cardImages';
import { GameBadge } from './ProductTile';
import GameCardArt from './GameCardArt';
import { isProductCardId, gameOfId } from '../lib/games';
import { isAskingPrice, priceHint } from '../lib/priceLabels';

// WO-26: `gameBadge` (a game id) labels the card's game in the all-games search; other callers pass nothing.
const CardListItem = ({ card, bulkPricingData, pricingLoading, onCardClick = null, navigationContext = null, gameBadge = null }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency } = useCurrency();
  
  // Individual pricing state (fallback when bulk pricing is not available)
  const [pricingData, setPricingData] = useState(null);
  const [individualPricingLoading, setIndividualPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState(false);
  
  // Collection/wishlist state
  // NOTE: We intentionally do NOT auto-check card status on mount in list views.
  // Each CardListItem used to independently call getUserCollections + getUserWishlist,
  // causing N*2 API requests on every page (30 cards = 60 requests). Instead, we
  // optimistically show "Add" buttons and only check status on first interaction.
  const [inCollection, setInCollection] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || '';

  // Check if we have bulk pricing data for this card
  const cardId = card.id || card.card_id;
  const hasBulkPricing = bulkPricingData && bulkPricingData[cardId];
  // WO-26: singles of the other games (D2 ids) come from the search with their TCGplayer `primary_price` (null = none);
  // Pokemon's price endpoints do not know them, so they are not asked.
  const gameCard = isProductCardId(cardId) && card.primary_price !== undefined;

  // Lazy status check — only called on first user interaction with collection/wishlist buttons
  const checkUserCardStatus = async () => {
    if (!user || statusChecked) return
    setStatusChecked(true)
    
    try {
      const [collectionResponse, wishlistResponse] = await Promise.all([
        collectionAPI.getUserCollections(user.id),
        wishlistAPI.getUserWishlist(user.id)
      ])

      if (collectionResponse.data) {
        const isInCollection = collectionResponse.data.some(collection => 
          collection.items?.some(item => item.card_id === cardId)
        )
        setInCollection(isInCollection)
      }
      
      if (wishlistResponse.data) {
        setInWishlist(wishlistResponse.data.some(item => item.card_id === cardId))
      }
    } catch (error) {
      console.error('Error checking user card status:', error)
    }
  }

  // Fetch individual pricing data only if bulk pricing is not available
  useEffect(() => {
    let mounted = true;
    
    // Skip individual fetch if we have bulk pricing or it's currently loading
    if (hasBulkPricing || pricingLoading || gameCard) {
      return;
    }
    
    const fetchIndividualPricing = async () => {
      try {
        setIndividualPricingLoading(true);
        const response = await fetch(`${apiUrl}/api/card/${encodeURIComponent(cardId)}/primary-price`);
        
        if (response.ok) {
          const result = await response.json();
          if (mounted && result.success) {
            setPricingData(result.data);
          } else if (mounted) {
            setPricingError(true);
          }
        } else if (mounted) {
          setPricingError(true);
        }
      } catch (error) {
        if (mounted) {
          console.warn('Error fetching individual pricing for card:', cardId, error);
          setPricingError(true);
        }
      } finally {
        if (mounted) {
          setIndividualPricingLoading(false);
        }
      }
    };

    // Only fetch if we have a card ID and no bulk pricing
    if (cardId && !hasBulkPricing && !pricingLoading) {
      fetchIndividualPricing();
    }

    return () => {
      mounted = false;
    };
  }, [cardId, apiUrl, hasBulkPricing, pricingLoading, gameCard]);

  // Calculate primary price - prioritize bulk pricing, then individual API, then fallback to variants
  const primaryPrice = useMemo(() => {
    if (gameCard) return card.primary_price?.price > 0 ? card.primary_price.price : 0;
    // 1. Use bulk pricing data if available
    if (hasBulkPricing && bulkPricingData[cardId].success && bulkPricingData[cardId].price > 0) {
      return bulkPricingData[cardId].price;
    }
    
    // 2. Use individual API data if available
    if (pricingData && pricingData.price > 0) {
      return pricingData.price;
    }
    
    // 3. Fallback to static variants calculation
    if (!card.variants || Object.keys(card.variants).length === 0) return 0;
    
    const prices = Object.values(card.variants)
      .filter(variant => variant && variant.price !== null && variant.price !== undefined && variant.price > 0)
      .map(variant => variant.price);
    
    return prices.length > 0 ? Math.min(...prices) : 0;
  }, [bulkPricingData, cardId, hasBulkPricing, pricingData, card.variants, gameCard, card.primary_price]);

  // Function to navigate to card detail page
  const goToCardDetail = () => {
    // Handle both possible ID property names for robustness
    const cardId = card.id || card.card_id;
    if (cardId) {
      if (typeof onCardClick === 'function') {
        onCardClick(card);
      }
      navigate(`/card/${encodeURIComponent(cardId)}`, navigationContext ? { state: navigationContext } : undefined);
    } else {
      console.error('Card ID not found:', card);
    }
  };

  // Function to capitalize text
  const capitalize = (text) => {
    if (!text) return '';
    return text.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const renderPrice = (price) => {
    if (gameCard) {   // never a made-up $0.00: no TCGplayer price is "No price yet"
      if (!(price > 0)) return <span className="text-xs text-cream/40 font-normal">No price yet</span>;
      return (
        <span title={priceHint(card.primary_price?.price_type)}>
          <PriceDisplay amountCad={price} />
          {isAskingPrice(card.primary_price?.price_type) && (
            <span className="ml-1 rounded bg-dark border border-purple/40 px-1 text-[10px] text-cream/70 align-middle">Asking</span>
          )}
        </span>
      );
    }
    // Show loading state if bulk pricing is loading or individual pricing is loading
    const isLoading = pricingLoading || individualPricingLoading;
    
    if (isLoading) return 'Loading...';
    
    // Show error state only if no pricing data is available anywhere
    const hasAnyPricing = hasBulkPricing || pricingData || (card.variants && Object.keys(card.variants).length > 0);
    if (pricingError && !hasAnyPricing) return 'Price N/A';
    
    if (typeof price === 'number' && price > 0) {
      return <PriceDisplay amountCad={price} />;
    }

    return <PriceDisplay amountCad={0} />;
  };

  // Helper function to determine the correct image URL
  const getDisplayImageUrl = () => {
    // First priority: use nested images object (from new API)
    if (card.images?.large) {
      return card.images.large;
    }
    if (card.images?.small) {
      return card.images.small;
    }
    
    // Second priority: use flat image fields (legacy format)
    if (card.image_small) {
      return card.image_small;
    }
    if (card.image_large) {
      return card.image_large;
    }
    
    // Third priority: try the main card image
    if (card.cardImage) {
      return card.cardImage;
    }
    
    // Then try the logo image (commonly used)
    if (card.LogoImg) {
      return card.LogoImg;
    }
    
    // Fallback to backupLogoUrl
    if (card.backupLogoUrl) {
      return card.backupLogoUrl;
    }
    
    // Try the extLogoImg
    if (card.extLogoImg) {
      return card.extLogoImg;
    }
    
    // Final fallback to placeholder
    return '/placeholder-card.svg';
  };

  // Check if card is holo based on variants or rarity
  const isHolo = () => {
    if (card.variants && Object.keys(card.variants).length > 0) {
      return Object.keys(card.variants).some(key => key.toLowerCase().includes('holo'));
    }
    return card.rarity?.toLowerCase().includes('holo') || false;
  };

  // Check if card is rare based on rarity
  const isRare = () => {
    if (!card.rarity) return false;
    const rarityLower = card.rarity.toLowerCase();
    return rarityLower.includes('rare') || 
           rarityLower.includes('ultra') || 
           rarityLower.includes('secret') ||
           rarityLower.includes('full art');
  };
  
  const handleAddToCollection = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      const returnUrl = `/card/${encodeURIComponent(cardId)}`
      sessionStorage.setItem('redirectAfterLogin', returnUrl)
      navigate('/login')
      return
    }

    setActionLoading(true)
    try {
      // Lazy-check status on first click
      if (!statusChecked) {
        await checkUserCardStatus()
      }

      if (inCollection) {
        const collectionResponse = await collectionAPI.getUserCollections(user.id)
        if (collectionResponse.data) {
          const collectionWithCard = collectionResponse.data.find(collection => 
            collection.items?.some(item => item.card_id === cardId)
          )
          if (collectionWithCard) {
            await collectionAPI.removeFromCollection(collectionWithCard.id, cardId)
            setInCollection(false)
          }
        }
      } else {
        await collectionAPI.quickAddToCollection(cardId, {
          quantity: 1,
          condition: 'Near Mint',
          currency,
        })
        setInCollection(true)
      }
    } catch (error) {
      console.error('Error updating collection:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleAddToWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      const returnUrl = `/card/${encodeURIComponent(cardId)}`
      sessionStorage.setItem('redirectAfterLogin', returnUrl)
      navigate('/login')
      return
    }

    setActionLoading(true)
    try {
      // Lazy-check status on first click
      if (!statusChecked) {
        await checkUserCardStatus()
      }

      if (inWishlist) {
        await wishlistAPI.removeFromWishlist(cardId)
        setInWishlist(false)
      } else {
        await wishlistAPI.quickAddToWishlist(cardId, 2, currency)
        setInWishlist(true)
      }
    } catch (error) {
      console.error('Error updating wishlist:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Format card number as fraction if printedTotal exists
  const formatCardNumber = () => {
    if (!card.number) return '';
    return card.printedTotal ? `${card.number}/${card.printedTotal}` : card.number;
  };

  return (
    <div 
      className="bg-dark border border-purple/20 rounded-lg overflow-hidden hover:border-purple/60 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      onClick={goToCardDetail}
    >
      <div className="p-0">
        <div className="relative">
          {/* Card Image */}
          <div className="relative aspect-[2.5/3.5] overflow-hidden">
            <div className="flex items-center justify-center w-full h-full">
              {isProductCardId(cardId) ? (
                // WO-30: other games' singles: the mirrored image whole (object-contain: Scryfall's rules forbid
                // cropping the artist/copyright line), else the drawn card instead of the generic "Not Found" art
                <div className="w-[90%] h-[90%] rounded-md overflow-hidden">
                  <GameCardArt gameId={card.game || card.tcg || gameOfId(cardId)} name={card.name} number={card.number}
                               subtitle={card.set?.name || card.setName || card.set_name || card.rarity}
                               image={card.image_small || card.image_large || card.images?.small || card.images?.large}
                               fit="contain" photo={card.photo_url} photoCredit={card.photo_credit} photoHints={photoHintsOf(card)} />
                </div>
              ) : (
              <img 
                src={getDisplayImageUrl(card)} 
                alt={capitalize(card.name)} 
                className="object-cover transition-transform duration-500 group-hover:scale-105 w-[90%] h-[90%]"
                onError={imageFallback(sourceImageUrls(card))}
              />
              )}
            </div>
          </div>
          
          {/* Collection/Wishlist Buttons */}
          <div className="absolute top-2 right-2 flex gap-1">
            <button 
              className={`h-7 w-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                inCollection
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-purple/90 hover:bg-purple text-white'
              }`}
              onClick={handleAddToCollection}
              disabled={actionLoading}
              title={inCollection ? 'Remove from Collection' : 'Add to Collection'}
            >
              {actionLoading ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b border-current"></div>
              ) : inCollection ? (
                <Check size={12} />
              ) : (
                <Plus size={12} />
              )}
            </button>

            <button 
              className={`h-7 w-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                inWishlist
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-gray-900/70 backdrop-blur-sm hover:bg-gray-900/90 text-gray-100'
              }`}
              onClick={handleAddToWishlist}
              disabled={actionLoading}
              title={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
            >
              {actionLoading ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b border-current"></div>
              ) : (
                <Heart size={12} className={inWishlist ? 'fill-current' : ''} />
              )}
            </button>
          </div>
          
          {gameBadge && (
            <div className="absolute bottom-2 left-2" data-testid="game-badge"><GameBadge game={gameBadge} /></div>
          )}

          {/* Badges */}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {isHolo() && (
              <span className="inline-flex items-center rounded-md bg-gray-900/70 backdrop-blur-sm border border-purple-500/50 px-2 py-0.5 text-xs font-light text-blue-300 hover:bg-gray-900/80 tracking-wider">
                HOLO
              </span>
            )}
            {isRare() && (
              <span className="inline-flex items-center rounded-md bg-gray-900/70 backdrop-blur-sm border border-yellow-500/50 px-2 py-0.5 text-xs font-light text-yellow-300 hover:bg-gray-900/80 tracking-wider">
                RARE
              </span>
            )}
          </div>
        </div>
        
        {/* Card Info - Removed the background */}
        <div className="p-4 bg-transparent">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-medium text-gray-100 truncate tracking-wide">
              {capitalize(card.name)}
              <span className="ml-1 text-gray-400 text-xs">{formatCardNumber()}</span>
            </h3>
            <div className="flex items-center tooltip-trigger" data-tooltip={`HP: ${card.hp || 'N/A'}`}>
              {card.hp && (
                <div className="text-xs font-medium text-gray-300">
                  HP {card.hp}
                </div>
              )}
            </div>
          </div>
          
          <div className="text-xs text-gray-300/60 mb-3 font-light tracking-wide">
            {card.setName && <span>{capitalize(card.setName)}</span>}
            {card.setName && card.rarity && <span className="mx-1">•</span>}
            {card.rarity && <span>{capitalize(card.rarity)}</span>}
          </div>
          
          <div className="flex justify-between items-center">
            <div>
              <div className="text-blue-300 font-medium tracking-wide">
                {renderPrice(primaryPrice)}
              </div>
            </div>
            {/* Keep the View button for users who prefer clicking it explicitly */}
            <button 
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering the parent onClick
                goToCardDetail();
              }}
              className="inline-flex items-center justify-center rounded-md border border-purple-500/30 bg-transparent px-4 py-2 text-xs font-light text-gray-100 hover:border-purple-500 hover:text-blue-300 transition-colors tracking-wider"
            >
              VIEW
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardListItem;
